import { useCallback, useRef, useState } from "react";

import { useChat } from "@ai-sdk/react";
import {
  ChatOnFinishCallback,
  UIMessage,
  generateId,
  getToolName,
  isStaticToolUIPart,
  isTextUIPart,
  isToolUIPart,
  lastAssistantMessageIsCompleteWithApprovalResponses,
} from "ai";

import type { AssistantConfig } from "../contexts/AssistantConfigContext";
import { getConfiguredToolPartView } from "../Thread/ToolPart/helpers";

import { hasHandedOff } from "./feedbackRequest";
import type { AssistantChatTransport } from "./trpcChatTransport";

interface UseAssistantChatProps {
  transport: AssistantChatTransport;
  initial?: { messages: UIMessage[]; canResume: boolean };
  config: Pick<AssistantConfig, "getToolPartDisplay" | "toolRenderers">;
  buildMessageParts?: (text: string) => UIMessage["parts"];
  onFinish?: ChatOnFinishCallback<UIMessage>;
  onError?: (error: Error) => void;
  onToolRendered?: (params: { toolName: string }) => void;
}

export const useAssistantChat = ({
  transport,
  initial,
  config,
  buildMessageParts = buildTextParts,
  onFinish,
  onError,
  onToolRendered,
}: UseAssistantChatProps) => {
  const [input, setInput] = useState("");
  const [hasResumed, setHasResumed] = useState(false);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [isStopping, setIsStopping] = useState(false);
  const renderedToolCallIds = useRef(new Set<string>());

  const chat = useChat({
    transport,
    messages: initial?.messages,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    onFinish: (event) => {
      setActiveTurnId(null);
      event.message.parts.filter(isStaticToolUIPart).forEach((part) => {
        const view = getConfiguredToolPartView(part, config);
        if ((view === "output" || view === "approval") && !renderedToolCallIds.current.has(part.toolCallId)) {
          renderedToolCallIds.current.add(part.toolCallId);
          onToolRendered?.({ toolName: getToolName(part) });
        }
      });
      onFinish?.(event);
    },
    onError,
  });

  const isBusy = chat.status === "submitted" || chat.status === "streaming";

  const hasPendingApproval =
    chat.messages.at(-1)?.parts.some((part) => isToolUIPart(part) && part.state === "approval-requested") ?? false;
  const canResume = Boolean(
    initial?.canResume &&
    (!hasResumed || chat.status === "error") &&
    chat.messages.at(-1)?.id === initial.messages.at(-1)?.id
  );
  const isHandedOff = hasHandedOff(chat.messages);
  const isComposerDisabled = isBusy || hasPendingApproval || canResume || isStopping || isHandedOff;
  const canStop = isBusy && !hasPendingApproval && chat.messages.some((message) => message.id === activeTurnId);

  const sendTurn = useCallback(
    (parts: UIMessage["parts"]) => {
      const id = generateId();
      setActiveTurnId(id);
      void chat.sendMessage({ id, parts });
    },
    [chat]
  );

  const submit = useCallback(
    (suggestion?: string) => {
      const text = (suggestion ?? input).trim();
      if (!text || isComposerDisabled) {
        return;
      }
      if (suggestion === undefined) {
        setInput("");
      }
      sendTurn(buildMessageParts(text));
    },
    [sendTurn, input, isComposerDisabled, buildMessageParts]
  );

  const answerApproval = useCallback(
    (approvalId: string, approved: boolean, note?: string) => {
      setActiveTurnId(null);
      void chat.addToolApprovalResponse({ id: approvalId, approved, reason: note });
    },
    [chat]
  );

  const stop = async () => {
    const turn = chat.messages.find((message) => message.id === activeTurnId);
    if (!canStop || !turn) {
      return;
    }
    setActiveTurnId(null);
    await chat.stop();
    chat.setMessages((messages) => {
      const turnIndex = messages.findIndex((message) => message.id === turn.id);
      return turnIndex < 0 ? messages : messages.slice(0, turnIndex);
    });
    setInput(
      turn.parts
        .filter(isTextUIPart)
        .map((part) => part.text)
        .join("\n")
    );
    setIsStopping(true);
    await transport.cancelTurn(turn.id).finally(() => setIsStopping(false));
  };

  return {
    getAiSessionId: () => transport.getAiSessionId(),
    messages: chat.messages,
    canResume,
    resume: () => {
      setHasResumed(true);
      void chat.resumeStream();
    },
    status: chat.status,
    error: chat.error,
    errorCode: chat.error?.message ?? null,
    isBusy,
    isComposerDisabled,
    isHandedOff,
    canStop,
    stop,
    input,
    setInput,
    submit,
    sendTurn,
    answerApproval,
  };
};

export type AssistantChat = ReturnType<typeof useAssistantChat>;

const buildTextParts = (text: string): UIMessage["parts"] => [{ type: "text", text }];
